import { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../config/api';
import type { PlanetaryPositionsResponse, PlanetaryPositionsRequestParams } from '../types/api';

interface UsePlanetaryPositionsResult {
  data: PlanetaryPositionsResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const API_TIMEOUT_MS = 15000;

export const usePlanetaryPositions = (params?: PlanetaryPositionsRequestParams): UsePlanetaryPositionsResult => {
  const [data, setData] = useState<PlanetaryPositionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

    const doFetch = async () => {
      setLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams();
        if (params?.date) queryParams.append('date', params.date);
        if (params?.time) queryParams.append('time', params.time);
        if (params?.latitude !== undefined) queryParams.append('latitude', params.latitude.toString());
        if (params?.longitude !== undefined) queryParams.append('longitude', params.longitude.toString());
        if (params?.altitude !== undefined) queryParams.append('altitude', params.altitude.toString());

        const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.planetaryPositions}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Failed to fetch planetary positions: ${response.statusText}`);
        }

        const result: PlanetaryPositionsResponse = await response.json();
        if (!controller.signal.aborted) setData(result);
      } catch (err) {
        if (controller.signal.aborted) return; // Unmounted or superseded — discard silently
        const msg = err instanceof DOMException && err.name === 'AbortError'
          ? 'Request timed out. Please try again.'
          : err instanceof Error ? err.message : 'An unknown error occurred';
        setError(msg);
        setData(null);
      } finally {
        clearTimeout(timeoutId);
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    doFetch();
    return () => { controller.abort(); clearTimeout(timeoutId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.date, params?.time, params?.latitude, params?.longitude, params?.altitude, fetchTrigger]);

  const refetch = useCallback(() => setFetchTrigger(n => n + 1), []);

  return {
    data,
    loading,
    error,
    refetch,
  };
};
